/** Danmu lane: fans' takes and reactions fly across the court at the moment they answered. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Take } from '@clutch/core';
import { dueDanmu, laneFor, type DanmuItem } from './danmu';
import { FaceThumb } from '../studio/Studio';
import { initials } from './derive';

export type Flying = DanmuItem & { key: number; lane: number; replay: boolean };

const FLY_MS = 6500;

/**
 * Schedules items: anything whose seq the game passes surfaces (replay), and `push` shows an
 * item right now (a take that was just posted).
 */
export function useDanmu(items: readonly DanmuItem[], asOfSeq: number) {
  const [flying, setFlying] = useState<Flying[]>([]);
  const prev = useRef<number | undefined>(undefined);
  const n = useRef(0);
  const launch = useCallback((xs: DanmuItem[], replay: boolean) => {
    if (!xs.length) return;
    const now = xs.map((x) => ({ ...x, key: ++n.current, lane: laneFor(n.current), replay }));
    setFlying((f) => [...f.slice(-8), ...now]);
    const keys = new Set(now.map((x) => x.key));
    setTimeout(() => setFlying((f) => f.filter((x) => !keys.has(x.key))), FLY_MS + 200);
  }, []);
  useEffect(() => {
    const p = prev.current;
    prev.current = asOfSeq;
    if (p === undefined) return; // first snapshot: nothing "passes", the past stays in the past
    launch(dueDanmu(items, p, asOfSeq).slice(0, 4), true);
  }, [asOfSeq]); // only the game moving triggers the lane
  return { flying, push: (x: DanmuItem) => launch([x], false) };
}

export function DanmuLane({
  flying,
  takeById,
  urlOf,
  onOpenTake,
}: {
  flying: Flying[];
  takeById: Map<string, Take>;
  urlOf: (t: Take) => string;
  onOpenTake: (t: Take) => void;
}) {
  if (!flying.length) return null;
  return (
    <div className="danmu" aria-live="polite" data-testid="danmu">
      {flying.map((f) => {
        const take = f.takeId ? takeById.get(f.takeId) : undefined;
        return (
          <button
            key={f.key}
            className={`danmu__item danmu__item--${f.kind}`}
            style={{ top: `${10 + f.lane * 34}px`, animationDuration: `${FLY_MS}ms` }}
            onClick={() => take && onOpenTake(take)}
            tabIndex={take ? 0 : -1}
            data-testid={`danmu-${f.kind}`}
          >
            {take ? <FaceThumb url={urlOf(take)} name={f.name} size={24} /> : <span className="danmu__av">{initials(f.name)}</span>}
            <span className="danmu__txt">
              {f.kind === 'take' ? (f.replay ? `${f.name}’s take` : `${f.name} posted a take`) : f.name}
            </span>
            {f.emoji && <span className="danmu__emoji">{f.emoji}</span>}
            {take && <span className="danmu__play">▶</span>}
          </button>
        );
      })}
    </div>
  );
}
